import compact from 'lodash/compact';
import without from 'lodash/without';
import xor from 'lodash/xor';

import type { TextTheme } from '@wix/editor-platform-sdk-types';
import type { AggregatedStyleValues } from '../../common/abstractStyles/types';
import { TRANSLATIONS, WEIGHT_LABELS } from '../../constants';
import { getCurrentDecomposedValues } from '../../common/abstractStyles/decompositionUtils';
import { getShorthandAtomics } from '../../common/grouping/propertyRelationships';

/**
 * Normalize the `selectTextTheme` response into a plain text theme.
 *
 * Studio2's host currently returns the text-theme object directly instead of
 * the standard `{ result }` wrapper. This is a known host-side bug that will be
 * fixed later; until then, unwrap defensively so both shapes are supported.
 */
export function unwrapTextThemeResult(
  response: unknown,
): TextTheme | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  if ('result' in response) {
    return (response as { result?: TextTheme }).result;
  }

  return response as TextTheme;
}

export type TextPropertyEntry = {
  value: string;
  resolvedValue: string;
};

export type PropertyMap = Record<string, TextPropertyEntry>;

export const getFontWeightLabel = (w: string | undefined): string =>
  (w && WEIGHT_LABELS[w]) ?? w ?? TRANSLATIONS.FONT_WEIGHT_REGULAR;

/**
 * Build cssPropertyType → entry map from BOTH decomposed namespaces.
 *
 * cssProperties: key IS the cssPropertyType.
 * cssCustomProperties: key is the var name; cssPropertyType is in definitions.
 */
export function buildPropertyMap(values: AggregatedStyleValues): PropertyMap {
  const map: PropertyMap = {};

  const currentDecomposed = getCurrentDecomposedValues(values);

  Object.entries(currentDecomposed).forEach(([key, atomics]) => {
    const atomic = atomics[0] || { value: '', resolvedValue: '' };
    map[key] = {
      value: atomic.value,
      resolvedValue: atomic.resolvedValue ?? atomic.value,
    };
  });

  return map;
}

export function toggleTextDecorationValue(
  currentValue: string,
  token: string,
): string {
  const currentTokens = compact(currentValue.split(/\s+/));
  const validTokens = without(currentTokens, 'none');

  const nextTokens = xor(validTokens, [token]);

  return nextTokens.length > 0 ? nextTokens.join(' ') : 'none';
}

/**
 * Check if 'font' is declared as a cssPropertyType in definitions.
 * The font shorthand is never in decomposed (it expands to atomics), so
 * eligibility for the text style picker must come from definitions directly.
 */
export function hasFontInDefinitions(values: AggregatedStyleValues): boolean {
  if (
    'cssProperties' in values.definitions &&
    'font' in values.definitions.cssProperties
  ) {
    return true;
  }
  if ('cssCustomProperties' in values.definitions) {
    return Object.values(values.definitions.cssCustomProperties).some(
      (def) => def.cssPropertyType === 'font',
    );
  }
  return false;
}

/**
 * Read the raw value of the 'font' property (e.g. "var(--font-0)").
 * The font shorthand is never written to decomposed, so we read from raw directly.
 * Checks cssCustomProperties first (custom prop wins), then cssProperties.
 */
export function getFontRawValue(
  values: AggregatedStyleValues,
): string | undefined {
  if ('cssCustomProperties' in values.definitions) {
    const entry = Object.entries(values.definitions.cssCustomProperties).find(
      ([, def]) => def.cssPropertyType === 'font',
    );
    if (entry) {
      const [customPropKey] = entry;
      const value =
        'cssCustomProperties' in values.raw
          ? values.raw.cssCustomProperties[customPropKey]
          : undefined;
      if (value !== undefined) {
        return value;
      }
    }
  }

  return 'cssProperties' in values.raw
    ? values.raw.cssProperties.font
    : undefined;
}

export const normalizeUnit = (val: string | undefined): string | undefined =>
  val?.replace('spx', 'px').replace('px*', 'px') || undefined;

const FONT_EXPANDED_KEYS = getShorthandAtomics('font');

/**
 * Check if any direct font atomics (fontSize, fontFamily, etc.) coexist
 * alongside the font shorthand in raw values.
 * Mirrors editor-elements' getHasFontExpandedProperties — presence-based,
 * no value comparison.
 */
export function hasFontExpandedProperties(
  values: AggregatedStyleValues,
): boolean {
  if (!hasFontInDefinitions(values)) {
    return false;
  }

  const fontRaw = getFontRawValue(values);
  if (!fontRaw) {
    return false;
  }

  const cssProperties =
    'cssProperties' in values.raw ? values.raw.cssProperties : {};

  // Check cssProperties: raw keys ARE css property types (e.g. "fontSize")
  if ('cssProperties' in values.raw) {
    if (FONT_EXPANDED_KEYS.some((key) => key in cssProperties)) {
      return true;
    }
  }

  // Check cssCustomProperties: raw keys are var names (e.g. "--font-size"),
  // so we look up cssPropertyType from definitions to match against font longhands
  if (
    'cssCustomProperties' in values.raw &&
    'cssCustomProperties' in values.definitions
  ) {
    const customDefs = values.definitions.cssCustomProperties;
    return Object.keys(values.raw.cssCustomProperties).some((customKey) => {
      const cssPropertyType = customDefs[customKey]?.cssPropertyType;
      return (
        cssPropertyType !== undefined &&
        FONT_EXPANDED_KEYS.includes(cssPropertyType)
      );
    });
  }

  return false;
}

export const TEXT_PROPERTY_CONFIG = [
  { resultKey: 'color', mixedKey: 'isMixedFontColor', atomicKeys: 'color' },
  {
    resultKey: 'fontSize',
    mixedKey: 'isMixedFontSize',
    atomicKeys: 'fontSize',
  },
  {
    resultKey: 'fontFamily',
    mixedKey: 'isMixedFontFamily',
    atomicKeys: 'fontFamily',
  },
  {
    resultKey: 'fontWeight',
    mixedKey: 'isMixedFontWeight',
    atomicKeys: 'fontWeight',
  },
  {
    resultKey: 'fontStyle',
    mixedKey: 'isMixedFontStyle',
    atomicKeys: 'fontStyle',
  },
] as const;
